// Sentry + LogRocket — MUST be imported before anything else so their
// hooks (fetch, xhr, unhandledrejection) register first.
import "@/instrumentation";
import { Sentry } from "@/instrumentation";
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { installLegalTermProtection } from "@/lib/legalTerms";

// Guard Canadian legal acronyms (TAL, RBQ, Loi 41, T776, N4, ...) from
// being butchered by Google Translate when French mode is active.
installLegalTermProtection();

// Swallow axios rejections AND uncaught axios errors so the React
// dev-overlay doesn't flash for expected non-2xx (401/402/403/404)
// responses. Components handle them individually with try/catch — this
// is purely cosmetic for the preview environment.
//
// PLUS: app-wide safety net for ANY OTHER uncaught runtime error thrown
// inside a click handler / event listener / async function. React can't
// catch async errors that never return through render, so without this,
// a single throw inside an onClick would silently kill the interaction
// and leave the button "spinning" forever. We:
//   1. Catch every uncaught rejection + runtime error
//   2. Show a friendly toast (not a scary dev-overlay)
//   3. Post the crash to /api/client-errors so it appears in /admin/error-log
if (typeof window !== "undefined") {
  const isAxiosErr = (r) => r && (r.isAxiosError || r?.response || r?.config || /Request failed with status code/i.test(r?.message || ""));

  // "Noise" = third-party script failures, ad-blocker aborts, cross-origin
  // script errors, tracker pixels, Sentry / LogRocket ingest getting
  // blocked in the preview iframe, etc. These do NOT affect the user's
  // interaction with our app, so we silently DROP them — no toast, no
  // server log, no Sentry event. Anything not on this list is treated as
  // a real app bug and reported quietly.
  const NOISE_PATTERNS = [
    /Script error\.?$/i,                     // cross-origin script (no info anyway)
    /Load failed/i,                          // Safari network abort
    /Failed to fetch/i,                      // generic fetch abort
    /NetworkError when attempting/i,         // Firefox network abort
    /ChunkLoadError/i,                       // stale build cached — user needs refresh, we can't fix
    /Loading chunk .* failed/i,
    /Loading CSS chunk/i,
    /ResizeObserver/i,                       // benign Chrome warning
    /gtag|googletag|doubleclick|adsbygoogle/i,
    /logrocket|logr-in|lr-ingest/i,
    /sentry\.io|ingest\.us\.sentry|sentry-trace/i,
    /google-analytics|googletagmanager/i,
    /facebook\.net|fbevents/i,
    /connect\.facebook|facebook\.com\/tr/i,
    /cf-rum|cloudflareinsights/i,
    /The play\(\) request was interrupted/i, // <video> autoplay noise
    /AbortError/i,                           // aborted fetches (route change)
    /The operation was aborted/i,
    /^cancel(l)?ed$/i,
    /^undefined$/i,
    /Non-Error promise rejection captured/i,
    /^:undefined$/,                          // cross-origin script error with blanked details
    /^\[object Object\]$/,                   // opaque thrown object with no keys
  ];
  const isNoise = (err, extraCtx = "") => {
    const msg = String(err?.message || err || "").trim();
    const stack = String(err?.stack || "");
    const src = String(extraCtx || "");
    if (!msg) return true;
    return NOISE_PATTERNS.some((p) => p.test(msg) || p.test(stack) || p.test(src));
  };

  const API = process.env.REACT_APP_BACKEND_URL || "";
  const _reportedRecently = new Set();
  // Per-path counter — hard-caps how many crashes we report from a
  // single URL before short-circuiting. Prevents an infinitely-looping
  // component crash on `/login/landlord` or `/partners/<encoded>` from
  // flooding `db.client_errors` with thousands of near-duplicate rows.
  const _pathCounts = new Map(); // path → count in the current 5-min window
  const _sessionCap = { count: 0, max: 30 }; // hard cap per browser session
  const _PATH_WINDOW_MS = 5 * 60_000; // 5-min rolling window per path
  const _PATH_MAX = 3;                // max 3 reports per path per window
  const _pathResetAt = new Map();     // path → epoch ms of window start

  // Serialize an arbitrary thrown value into a useful signature string.
  // Handles: Error, {message}, {reason}, plain objects, Response, primitives.
  const signatureOf = (err) => {
    if (err == null) return "";
    if (typeof err === "string") return err.slice(0, 200);
    if (typeof err === "number" || typeof err === "boolean") return String(err);
    // React ErrorBoundary passes { componentStack }; unhandledrejection may
    // pass a Response or a plain object. Prefer message, then reason.message,
    // then a JSON snapshot of enumerable keys.
    const msg = err.message || err.reason?.message || err.error?.message || err.detail;
    if (msg) return String(msg).slice(0, 200);
    try {
      const snapshot = JSON.stringify(err, Object.getOwnPropertyNames(err).slice(0, 8));
      if (snapshot && snapshot !== "{}") return snapshot.slice(0, 200);
    } catch { /* fallthrough */ }
    // Response-like: capture URL + status.
    if (err.url || err.status) return `HTTP ${err.status || "?"} ${err.url || ""}`.trim().slice(0, 200);
    return String(err).slice(0, 200);
  };

  const reportUncaught = (kind, err, extraCtx = "") => {
    // Silently drop third-party / network / tracker noise.
    if (isNoise(err, extraCtx)) return;
    // Hard session cap — protects DB even if every guard below is
    // bypassed. Ops still see the FIRST 30 crashes per session, which
    // is plenty to diagnose any real issue.
    if (_sessionCap.count >= _sessionCap.max) return;

    const sig = signatureOf(err);
    // Skip empty or generic "[object Object]" signatures — they're never
    // actionable and just clutter the auto-improve digest.
    if (!sig || sig === "[object Object]" || _reportedRecently.has(sig)) return;

    // Per-path throttling — the crux of the "error storm" fix. Component
    // crashes tend to re-fire every re-render, so a single broken screen
    // can emit hundreds of `uncaught_error` posts per minute. Cap it at 3
    // per path per 5 minutes.
    const path = (typeof window !== "undefined" ? window.location.pathname : "/") || "/";
    const now = Date.now();
    const resetAt = _pathResetAt.get(path) || 0;
    if (now - resetAt > _PATH_WINDOW_MS) {
      _pathResetAt.set(path, now);
      _pathCounts.set(path, 0);
    }
    const currentPathCount = _pathCounts.get(path) || 0;
    if (currentPathCount >= _PATH_MAX) return;
    _pathCounts.set(path, currentPathCount + 1);

    // Signature dedup — extended from 60s → 5 min so a bug that fires
    // once per render loop doesn't clog the log inside its own throttle
    // window.
    _reportedRecently.add(sig);
    setTimeout(() => _reportedRecently.delete(sig), _PATH_WINDOW_MS);
    _sessionCap.count += 1;
    // Server log — fire-and-forget.
    try {
      fetch(`${API}/api/client-errors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}`,
        },
        body: JSON.stringify({
          kind,
          message: sig,
          stack: String(err?.stack || "").slice(0, 3000),
          path: window.location.pathname + window.location.search,
          user_agent: navigator.userAgent,
        }),
      }).catch(() => {});
    } catch { /* noop */ }
    // Sentry.
    try {
      if (err instanceof Error) {
        Sentry.captureException(err, { tags: { source: kind } });
      } else {
        Sentry.captureMessage(sig, { level: "error", tags: { source: kind } });
      }
    } catch { /* noop */ }
    // No user-facing toast. The ErrorBoundary already handles genuinely
    // broken screens; a global toast for every background error is more
    // alarming than helpful and was showing up on unrelated third-party
    // script failures in production. Silent reporting is enough.
  };

  window.addEventListener("unhandledrejection", (e) => {
    if (isAxiosErr(e?.reason)) { e.preventDefault(); return; }
    e.preventDefault();
    reportUncaught("uncaught_rejection", e?.reason);
  });

  window.addEventListener("error", (e) => {
    if (isAxiosErr(e?.error) || /Request failed with status code/i.test(e?.message || "")) {
      e.preventDefault();
      e.stopImmediatePropagation?.();
      return;
    }
    // Cross-origin script errors surface here with `e.error === null` and
    // no useful message/filename/lineno — the browser blanks the details
    // for security. Reporting them just produces opaque
    // ``{"stack":":undefined"}`` rows in db.client_errors that no one can
    // action. Drop them here.
    if (!e?.error && !e?.message && !e?.filename) return;
    // Extra context lets `isNoise` see the source filename for cross-origin
    // script errors that have no message.
    const src = String(e?.filename || "");
    reportUncaught("uncaught_error", e?.error || { message: e?.message, stack: `${src}:${e?.lineno}` }, src);
  }, true);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
